import { useMemo } from "react";
import { Stack } from "@mantine/core";

import { useGetRegistrations } from "../../../api/insights";
import { DailyAreaChart, StageFunnel, formatDay } from "../charts";
import SectionShell, { Stat, StatGrid, formatPct } from "../SectionShell";

function Registrations({ eventId }) {
  const { data, isPending, isError, error } = useGetRegistrations(eventId);

  const timelineData = useMemo(
    () =>
      (data?.registrationsOverTime ?? []).map((d) => ({
        day: formatDay(d.day),
        Registrations: d.count,
        Tickets: d.ticketCount,
      })),
    [data]
  );

  const funnel = data?.funnel;
  const stageValue = (key) =>
    data?.funnelStages?.find((stage) => stage.key === key)?.value;

  // approvalRate = approved registrations / all registrations created
  const totalRegistrations = (data?.registrationsOverTime ?? []).reduce(
    (sum, row) => sum + (Number(row.count) || 0),
    0
  );
  const approvalRate = totalRegistrations
    ? (funnel?.approved ?? 0) / totalRegistrations
    : null;

  const hours = Number(data?.avgHoursFromRegistrationToCheckIn);
  // Without a single check-in the whole area reads as 0% / 0.0 hours, which
  // looks like a bad result instead of a feature the event never used.
  const usedCheckIn = Number(stageValue("checkedIn")) > 0;

  const registrationStages = [
    { key: "pageOpened", label: "Page opened", value: funnel?.formOpened },
    { key: "started", label: "Started", value: funnel?.formStarted },
    { key: "completed", label: "Completed", value: funnel?.formCompleted },
    { key: "approved", label: "Approved", value: funnel?.approved },
  ];

  const attendanceStages = [
    {
      key: "ticketIssued",
      label: "Ticket issued",
      value: stageValue("ticketCreated"),
    },
    { key: "checkedIn", label: "Checked in", value: stageValue("checkedIn") },
  ];

  return (
    <SectionShell
      id="registrations"
      title="Registrations"
      description="From the registration form to on-site attendance."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="registrations"
    >
      <Stack gap="lg">
        <StatGrid>
          <Stat
            part="approvalRate"
            label="Approval rate"
            value={formatPct(approvalRate)}
          />
          {usedCheckIn && (
            <>
              <Stat
                part="checkInRate"
                label="Check-in rate"
                value={formatPct(data?.registrationToAttendanceRate)}
              />
              <Stat
                part="avgHoursToCheckIn"
                label="Avg hours to check-in"
                value={Number.isFinite(hours) ? hours.toFixed(1) : "—"}
              />
            </>
          )}
        </StatGrid>

        <StageFunnel
          part="registrationFunnel"
          title="Registration funnel"
          stages={registrationStages}
        />
        {usedCheckIn && (
          <StageFunnel
            part="attendance"
            title="Attendance"
            stages={attendanceStages}
          />
        )}
        <DailyAreaChart
          part="registrationsOverTime"
          title="Registrations and tickets over time"
          data={timelineData}
          series={[
            { name: "Registrations", color: "blue.6" },
            { name: "Tickets", color: "green.6" },
          ]}
        />
      </Stack>
    </SectionShell>
  );
}

export default Registrations;
